// client/apps/public-form/src/components/MultiStepForm/ProgressBar.tsx
import React from 'react';
import * as FeatherIcons from 'react-feather'; // Import all icons
import { FormStep } from '../../types';
// Adjust import path based on where you placed Icon.tsx
import Icon from '../Icon'; // Or '../Icon'
import './ProgressBar.scss';

// Helper function to convert 'fe-kebab-case' to 'PascalCase' expected by react-feather
// (Place this here or in a shared utils file)
function kebabToPascalCase(kebab: string): keyof typeof FeatherIcons {
    const nameWithoutPrefix = kebab.startsWith('fe-') ? kebab.slice(3) : kebab;
    const pascal = nameWithoutPrefix
        .split('-')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join('');
    // Basic check if the name exists in FeatherIcons, otherwise default
    return pascal in FeatherIcons ? pascal as keyof typeof FeatherIcons : 'HelpCircle';
}


interface ProgressBarProps {
  steps: FormStep[];
  currentStepIndex: number;
}

function ProgressBar({ steps, currentStepIndex }: ProgressBarProps) {
  // Calculate progress percentage for mobile view
  const progressPercentage = ((currentStepIndex + 1) / steps.length) * 100;
  const currentStepName = steps[currentStepIndex]?.title || '';

  return (
    <div
      className="progress-bar"
      data-current-step={currentStepIndex + 1}
      data-total-steps={steps.length}
      data-step-name={currentStepName}
      style={{ '--progress-percentage': `${progressPercentage}%` } as React.CSSProperties}
    >
      {steps.map((step, index) => {
        const isActive = index === currentStepIndex;
        const isCompleted = index < currentStepIndex;
        // --- Convert kebab-case name to PascalCase ---
        const iconComponentName = kebabToPascalCase(step.iconName);

        let iconColor = 'var(--text-secondary)'; // Default color from variable
        if (isCompleted || isActive) {
          iconColor = '#ffffff'; // White icon for completed/active states
        }

        return (
          <React.Fragment key={step.id}>
            <div className={`progress-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
              <div className="step-icon-wrapper">
                {/* --- Use the Icon component --- */}
                <Icon name={iconComponentName} size={20} className="step-icon" color={iconColor} />
              </div>
              <div className="step-title">{step.title}</div>
            </div>
            {index < steps.length - 1 && (
              <div className={`progress-connector ${isCompleted ? 'completed' : ''}`}></div>
            )}
          </React.Fragment>
        );
      })}
      {/* Linear progress track for mobile view */}
      <div className="progress-bar-track"></div>
    </div>
  );
}

export default ProgressBar;